import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Preloader, f7 } from 'framework7-react';
import II18 from '../../translate/II18';
import { showPopupBlockedHelpDialog } from './popupBlockedHelp';

export const QES_STEP_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  AWAITING_USER: 'awaiting_user',
  BLOCKED: 'blocked',
  DONE: 'done',
  ERROR: 'error'
};

const muted = '#999';
const normal = '#0083e5';
const orange = '#e67e22';
const danger = '#c0392b';
const success = '#27ae60';

function stepColor(status) {
  switch (status) {
    case QES_STEP_STATUS.PENDING:
      return muted;
    case QES_STEP_STATUS.AWAITING_USER:
      // Stronger than hint (#999) so label/hint don’t blend.
      return normal;
    case QES_STEP_STATUS.BLOCKED:
      return orange;
    case QES_STEP_STATUS.ERROR:
      return danger;
    case QES_STEP_STATUS.DONE:
      return success;
    default:
      return normal;
  }
}

function StepIcon({ status }) {
  switch (status) {
    case QES_STEP_STATUS.RUNNING:
      return <Preloader size={18} />;
    case QES_STEP_STATUS.DONE:
      return (
        <Icon material="check_circle" size={21} style={{ color: success }} />
      );
    case QES_STEP_STATUS.BLOCKED:
    case QES_STEP_STATUS.ERROR:
      return <Icon material="warning" size={21} style={{ color: orange }} />;
    case QES_STEP_STATUS.AWAITING_USER:
      return (
        <Icon
          f7="arrow_right_circle_fill"
          size={21}
          style={{ color: normal }}
        />
      );
    case QES_STEP_STATUS.PENDING:
    default:
      return (
        <span
          style={{ display: 'inline-block', width: 18, textAlign: 'center' }}
        >
          ○
        </span>
      );
  }
}

StepIcon.propTypes = {
  status: PropTypes.string.isRequired
};

function HintText({ hint, showHelp }) {
  const onHelpClick = e => {
    e.preventDefault();
    e.stopPropagation();
    showPopupBlockedHelpDialog(f7);
  };

  return (
    <div
      style={{
        marginTop: 4,
        fontSize: 12,
        fontWeight: 400,
        color: muted,
        lineHeight: 1.35
      }}
    >
      {typeof hint === 'string' ? <II18>{hint}</II18> : hint}
      {showHelp && (
        <button
          type="button"
          id="qes_popup_blocked_help"
          aria-label={II18.get('signme_settings_nopopup_title')}
          onClick={onHelpClick}
          style={{
            display: 'contents',
            verticalAlign: 'middle',
            marginLeft: 4,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: '#595959',
            cursor: 'pointer',
            lineHeight: 1
          }}
        >
          <Icon material="help_outline" size={18} />
        </button>
      )}
    </div>
  );
}

HintText.propTypes = {
  hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  showHelp: PropTypes.bool
};

/**
 * Shared QES provider-modal step list.
 * Optional `action` under a step (e.g. START) when awaiting / blocked /
 * running (e.g. ATRUST registration status-check with restart CTA).
 * Optional `hintHelp` shows a ? that opens browser-specific pop-up instructions.
 * Optional `framed` wraps the step (+ action) in a blue rounded outline without
 * shifting the icon/text column relative to non-framed rows.
 * Optional `hintWhenDone` shows `hint` under a DONE step (e.g. collapsed prep).
 * @param {Array<{ id: string, label: string|React.ReactNode, status: string, hint?: string|React.ReactNode, hintHelp?: boolean, hintWhenDone?: boolean, framed?: boolean, action?: React.ReactNode }>} steps
 */
export default function QesStepList({ steps, style }) {
  if (!steps?.length) return null;

  return (
    <div
      className="qes-step-list"
      style={{ width: '100%', maxWidth: 360, ...(style || {}) }}
    >
      {steps.map(step => {
        const color = stepColor(step.status);
        const framed = Boolean(step.framed);
        const showAction =
          step.action &&
          (step.status === QES_STEP_STATUS.AWAITING_USER ||
            step.status === QES_STEP_STATUS.BLOCKED ||
            step.status === QES_STEP_STATUS.RUNNING);
        const showHint =
          step.hint &&
          (step.status === QES_STEP_STATUS.AWAITING_USER ||
            step.status === QES_STEP_STATUS.BLOCKED ||
            step.status === QES_STEP_STATUS.ERROR ||
            (step.status === QES_STEP_STATUS.RUNNING && step.action) ||
            (step.status === QES_STEP_STATUS.DONE && step.hintWhenDone));
        return (
          <div
            key={step.id}
            id={`qes_step_${step.id}`}
            className={
              framed
                ? 'qes-step-list__step qes-step-list__step--framed'
                : 'qes-step-list__step'
            }
            style={framed ? undefined : { padding: '8px 0' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                color
              }}
            >
              <span
                style={{
                  width: 24,
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 1
                }}
              >
                <StepIcon status={step.status} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color
                  }}
                >
                  {typeof step.label === 'string' ? (
                    <II18>{step.label}</II18>
                  ) : (
                    step.label
                  )}
                </div>
                {showHint && (
                  <HintText
                    hint={step.hint}
                    showHelp={Boolean(step.hintHelp)}
                  />
                )}
              </div>
            </div>
            {showAction && (
              <div
                className="qes-step-list__action"
                style={{
                  marginTop: framed ? undefined : 40,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                {step.action}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

QesStepList.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      status: PropTypes.oneOf(Object.values(QES_STEP_STATUS)).isRequired,
      hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      hintHelp: PropTypes.bool,
      hintWhenDone: PropTypes.bool,
      framed: PropTypes.bool,
      action: PropTypes.node
    })
  ),
  style: PropTypes.object
};
